import axios from "axios";
import { Edit, Eye, MoreVertical, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import Heading from "../../../components/AdminSideComponents/Heading";
import AddStaffDrawer from "../../../components/AdminSideComponents/staff/AddStaffDrawer";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { formatDate } from "../../../utils/helpers";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";

const ITEMS_PER_PAGE = 10;

const StaffManagement = () => {
  const { admin, isAdmin, isManager } = useAdminAuth();
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [actionOpen, setActionOpen] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const navigate = useNavigate();

  const fetchStaff = async () => {
    setIsLoading(true);
    try {
      const params = {};
      // Manager always gets scoped data from backend
      if (isManager) {
        params.branchId = admin?.assignedBranch?._id;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admins/get-all-staff`,
        { params },
      );
      const data = res.data?.data || [];
      setStaff(data);
      setFilteredStaff(data);
      setCurrentPage(1);
    } catch {
      showError("Failed to fetch staff");
      setStaff([]);
      setFilteredStaff([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleSearch = (term) => {
    if (!term.trim()) {
      applyBranchFilter(selectedBranch, staff);
      return;
    }
    const lower = term.toLowerCase();
    const searched = staff.filter(
      (s) =>
        s.name?.toLowerCase().includes(lower) ||
        s.email?.toLowerCase().includes(lower) ||
        s.phoneNumber?.includes(lower) ||
        s.designation?.toLowerCase().includes(lower),
    );
    applyBranchFilter(selectedBranch, searched);
  };

  const applyBranchFilter = (branchId, baseData = staff) => {
    if (!branchId) {
      setFilteredStaff(baseData);
    } else {
      setFilteredStaff(
        baseData.filter((s) => s.assignedBranch?._id === branchId),
      );
    }
    setCurrentPage(1);
  };

  const handleDeleteStaff = async (s) => {
    const confirmed = await confirmAction({
      title: "Delete Staff?",
      text: `This will permanently remove ${s.name}`,
      confirmText: "Delete",
    });
    if (!confirmed) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admins/delete/${s._id}`,
      );
      showSuccess("Staff deleted");
      fetchStaff();
    } catch {
      showError("Failed to delete staff");
    }
  };

  const totalPages = Math.ceil(filteredStaff.length / ITEMS_PER_PAGE);

  const paginatedStaff = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredStaff.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredStaff, currentPage]);

  const renderBranchFilter = () => {
    if (!isAdmin) return null;
    return (
      <div className="relative">
        <select
          value={selectedBranch}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedBranch(value);
            applyBranchFilter(value);
          }}
          className="
          min-w-[220px]
          px-4 py-2.5
          border border-gray-300
          rounded-lg
          bg-white
          shadow-md
          text-sm
          focus:outline-none
          focus:ring-1
          focus:ring-primaryColor
          transition
        "
        >
          <option value="">All Branches</option>
          {[
            ...new Map(
              staff
                .filter((s) => s.assignedBranch)
                .map((s) => [s.assignedBranch._id, s.assignedBranch]),
            ).values(),
          ].map((b) => (
            <option key={b._id} value={b._id}>
              {b.branchName}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className="py-2">
        <Breadcrumb title1="Staff Management" title2="Staff" />
      </div>
      <div className=" mb-6 bg-white rounded-md shadow-lg p-6">
        <Heading
          onSearch={handleSearch}
          showButton
          buttonName="Add Staff"
          customActions={renderBranchFilter}
          onButtonClick={() => setIsDrawerOpen(true)}
        />
        <div className="overflow-x-auto overflow-y-visible no-scrollbar">
          <table className="w-full table-fixed border border-gray-200 text-sm">
            <thead className="bg-[#EDB5A4] text-xs uppercase">
              <tr>
                <th className="p-3 w-12 text-center">#</th>
                <th className="p-3 w-40">Name</th>
                <th className="p-3 w-32">Phone</th>
                <th className="p-3 w-40">Email</th>
                {isAdmin && <th className="p-3 w-40">Branch</th>}
                <th className="p-3 w-32">Staff Since</th>
                <th className="p-3 w-32">Designation</th>
                {!isAdmin && <th className="p-3 w-56">Address</th>}
                <th className="p-3 w-20 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center animate-pulse">
                    Loading staff…
                  </td>
                </tr>
              ) : paginatedStaff.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    No staff found
                  </td>
                </tr>
              ) : (
                paginatedStaff.map((s, index) => (
                  <tr key={s._id} className="hover:bg-gray-50">
                    <td className="p-3 text-center">
                      {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                    </td>
                    <td
                      className="p-3 cursor-pointer"
                      onClick={() => {
                        navigate(`/staff-management/staff/${s._id}`);
                        window.scrollTo(0, 0);
                      }}
                    >
                      {s.name}
                    </td>
                    <td className="p-3">{s.phoneNumber}</td>
                    <td className="p-3 truncate">{s.email}</td>
                    {isAdmin && (
                      <td className="p-3 truncate">
                        {s.assignedBranch?.branchName || "-"}
                      </td>
                    )}
                    <td className="p-3">{formatDate(s.createdAt)}</td>
                    <td className="p-3 text-center">{s.designation}</td>
                    {!isAdmin && <td className="p-3 truncate">{s.address}</td>}
                    <td className="relative text-center">
                      <button
                        onClick={() =>
                          setActionOpen((p) => (p === s._id ? null : s._id))
                        }
                        className="p-2 rounded-lg hover:bg-gray-200"
                      >
                        <MoreVertical size={18} />
                      </button>

                      {actionOpen === s._id && (
                        <>
                          <div
                            className="fixed inset-0 z-[999]"
                            onClick={() => setActionOpen(null)}
                          />

                          {/* Dropdown – very high z-index + portal-like behavior */}
                          <div
                            className="
                              fixed z-[1000]
                              bg-white rounded-lg shadow-xl border
                              min-w-[160px] py-1
                            "
                            style={{
                              top: "auto",
                              left: "auto",
                              right: "72px",
                            }}
                          >
                            <button
                              onClick={() => {
                                navigate(`/staff-management/staff/${s._id}`);
                                window.scrollTo(0, 0);
                                setActionOpen(null);
                              }}
                              className="w-full px-4 py-3 hover:bg-gray-100 flex items-center gap-2 text-left"
                            >
                              <Eye size={16} /> View
                            </button>
                            <button
                              onClick={() => {
                                setEditStaff(s);
                                setIsDrawerOpen(true);
                                setActionOpen(null);
                              }}
                              className="w-full px-4 py-3 hover:bg-blue-50 text-blue-600 flex items-center gap-2 text-left"
                            >
                              <Edit size={16} /> Edit
                            </button>
                            <button
                              onClick={() => {
                                handleDeleteStaff(s);
                                setActionOpen(null);
                              }}
                              className="w-full px-4 py-3 hover:bg-red-50 text-red-600 flex items-center gap-2 text-left"
                            >
                              <Trash2 size={16} /> Delete
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-end gap-2 mt-4">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-4 py-2 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-4 py-2 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <AddStaffDrawer
        isOpen={isDrawerOpen}
        editingStaff={editStaff}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditStaff(null);
        }}
        onSuccess={fetchStaff}
      />
    </div>
  );
};

export default StaffManagement;

// import axios from "axios";
// import { Edit, Eye, MoreVertical, Trash2 } from "lucide-react";
// import { useEffect, useMemo, useState } from "react";

// import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
// import Heading from "../../../components/AdminSideComponents/Heading";
// import AddStaffDrawer from "../../../components/AdminSideComponents/staff/AddStaffDrawer";
// import ViewStaffDetailsModal from "../../../components/AdminSideComponents/staff/ViewStaffDetailsModal";

// import { formatDate } from "../../../utils/helpers";
// import {
//   confirmAction,
//   showError,
//   showSuccess,
// } from "../../../utils/toastUtils";

// import { useAdminAuth } from "../../../context/AdminAuthContext";

// const ITEMS_PER_PAGE = 10;

// const StaffManagement = () => {
//   const { admin, isAdmin, isManager } = useAdminAuth();

//   const [staff, setStaff] = useState([]);
//   const [filteredStaff, setFilteredStaff] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);

//   const [selectedBranch, setSelectedBranch] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [actionOpen, setActionOpen] = useState(null);

//   const [isDrawerOpen, setIsDrawerOpen] = useState(false);
//   const [viewStaff, setViewStaff] = useState(null);
//   const [editStaff, setEditStaff] = useState(null);

//   const fetchStaff = async () => {
//     setIsLoading(true);
//     try {
//       const params = {};

//       // Manager always gets scoped data from backend
//       if (isManager) {
//         params.branchId = admin?.assignedBranch;
//       }

//       const res = await axios.get(
//         `${process.env.REACT_APP_API_URL}/admins/get-all-staff`,
//         { params },
//       );

//       const data = res.data?.data || [];
//       setStaff(data);
//       setFilteredStaff(data);
//       setCurrentPage(1);
//     } catch {
//       showError("Failed to fetch staff");
//       setStaff([]);
//       setFilteredStaff([]);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchStaff();
//   }, []);

//   const handleSearch = (term) => {
//     if (!term.trim()) {
//       applyBranchFilter(selectedBranch, staff);
//       return;
//     }

//     const lower = term.toLowerCase();
//     const searched = staff.filter(
//       (s) =>
//         s.name?.toLowerCase().includes(lower) ||
//         s.email?.toLowerCase().includes(lower) ||
//         s.phoneNumber?.includes(lower) ||
//         s.designation?.toLowerCase().includes(lower),
//     );

//     applyBranchFilter(selectedBranch, searched);
//   };

//   const applyBranchFilter = (branchId, baseData = staff) => {
//     if (!branchId) {
//       setFilteredStaff(baseData);
//     } else {
//       setFilteredStaff(
//         baseData.filter((s) => s.assignedBranch?._id === branchId),
//       );
//     }
//     setCurrentPage(1);
//   };

//   const handleDeleteStaff = async (s) => {
//     const confirmed = await confirmAction({
//       title: "Delete Staff?",
//       text: `This will permanently remove ${s.name}`,
//       confirmText: "Delete",
//     });

//     if (!confirmed) return;

//     try {
//       await axios.delete(
//         `${process.env.REACT_APP_API_URL}/admins/delete/${s._id}`,
//       );
//       showSuccess("Staff deleted");
//       fetchStaff();
//     } catch {
//       showError("Failed to delete staff");
//     }
//   };

//   const totalPages = Math.ceil(filteredStaff.length / ITEMS_PER_PAGE);

//   const paginatedStaff = useMemo(() => {
//     const start = (currentPage - 1) * ITEMS_PER_PAGE;
//     return filteredStaff.slice(start, start + ITEMS_PER_PAGE);
//   }, [filteredStaff, currentPage]);

//   const renderBranchFilter = () => {
//     if (!isAdmin) return null;

//     return (
//       <div className="relative">
//         <select
//           value={selectedBranch}
//           onChange={(e) => {
//             const value = e.target.value;
//             setSelectedBranch(value);
//             applyBranchFilter(value);
//           }}
//           className="
//           min-w-[220px]
//           px-4 py-2.5
//           border border-gray-300
//           rounded-lg
//           bg-white
//           shadow-md
//           text-sm
//           focus:outline-none
//           focus:ring-1
//           focus:ring-primaryColor
//           transition
//         "
//         >
//           <option value="">All Branches</option>

//           {[
//             ...new Map(
//               staff
//                 .filter((s) => s.assignedBranch)
//                 .map((s) => [s.assignedBranch._id, s.assignedBranch]),
//             ).values(),
//           ].map((b) => (
//             <option key={b._id} value={b._id}>
//               {b.branchName}
//             </option>
//           ))}
//         </select>
//       </div>
//     );
//   };

//   return (
//     <>
//       <div className="px-4 py-3">
//         <Breadcrumb title1="Staff Management" title2="Staff" />
//       </div>

//       <div className="mx-4 mb-6 bg-white rounded-md shadow-lg p-6">
//         <Heading
//           onSearch={handleSearch}
//           showButton
//           buttonName="Add Staff"
//           customActions={renderBranchFilter}
//           onButtonClick={() => setIsDrawerOpen(true)}
//         />

//         <div className="overflow-x-auto overflow-y-visible no-scrollbar">
//           <table className="w-full table-fixed border border-gray-200 text-sm">
//             <thead className="bg-[#EDB5A4] text-xs uppercase">
//               <tr>
//                 <th className="p-3 w-12 text-center">#</th>
//                 <th className="p-3 w-40">Name</th>
//                 <th className="p-3 w-32">Phone</th>
//                 <th className="p-3 w-40">Email</th>
//                 {isAdmin && <th className="p-3 w-40">Branch</th>}
//                 <th className="p-3 w-32">Staff Since</th>
//                 <th className="p-3 w-32">Designation</th>
//                 {!isAdmin && <th className="p-3 w-56">Address</th>}
//                 <th className="p-3 w-20 text-center">Action</th>
//               </tr>
//             </thead>

//             <tbody className="divide-y">
//               {isLoading ? (
//                 <tr>
//                   <td colSpan={8} className="py-16 text-center">
//                     Loading staff…
//                   </td>
//                 </tr>
//               ) : paginatedStaff.length === 0 ? (
//                 <tr>
//                   <td colSpan={8} className="py-16 text-center">
//                     No staff found
//                   </td>
//                 </tr>
//               ) : (
//                 paginatedStaff.map((s, index) => (
//                   <tr key={s._id} className="hover:bg-gray-50">
//                     <td className="p-3 text-center">
//                       {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
//                     </td>
//                     <td
//                       className="p-3 cursor-pointer"
//                       onClick={() => setViewStaff(s)}
//                     >
//                       {s.name}
//                     </td>
//                     <td className="p-3">{s.phoneNumber}</td>
//                     <td className="p-3 truncate">{s.email}</td>
//                     {isAdmin && (
//                       <td className="p-3 truncate">
//                         {s.assignedBranch?.branchName || "-"}
//                       </td>
//                     )}
//                     <td className="p-3">{formatDate(s.createdAt)}</td>
//                     <td className="p-3 text-center">{s.designation}</td>

//                     {!isAdmin && <td className="p-3 truncate">{s.address}</td>}
//                     <td className="relative text-center">
//                       <button
//                         onClick={() =>
//                           setActionOpen((p) => (p === s._id ? null : s._id))
//                         }
//                         className="p-2 rounded-lg hover:bg-gray-200"
//                       >
//                         <MoreVertical size={18} />
//                       </button>

//                       {actionOpen === s._id && (
//                         <>
//                           <div
//                             className="fixed inset-0 z-10"
//                             onClick={() => setActionOpen(null)}
//                           />
//                           <div className="absolute right-10 top-8 w-48 bg-white rounded-lg shadow-xl border z-20">
//                             <button
//                               onClick={() => {
//                                 setViewStaff(s);
//                                 setActionOpen(null);
//                               }}
//                               className="w-full px-4 py-3 hover:bg-gray-100 flex gap-2"
//                             >
//                               <Eye size={16} /> View
//                             </button>
//                             <button
//                               onClick={() => {
//                                 setEditStaff(s);
//                                 setIsDrawerOpen(true);
//                                 setActionOpen(null);
//                               }}
//                               className="w-full px-4 py-3 hover:bg-blue-50 text-blue-600 flex gap-2"
//                             >
//                               <Edit size={16} /> Edit
//                             </button>
//                             <button
//                               onClick={() => handleDeleteStaff(s)}
//                               className="w-full px-4 py-3 hover:bg-red-50 text-red-600 flex gap-2"
//                             >
//                               <Trash2 size={16} /> Delete
//                             </button>
//                           </div>
//                         </>
//                       )}
//                     </td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>

//         {/* PAGINATION */}
//         {totalPages > 1 && (
//           <div className="flex justify-end gap-2 mt-4">
//             <button
//               disabled={currentPage === 1}
//               onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
//               className="px-4 py-2 border rounded disabled:opacity-50"
//             >
//               Prev
//             </button>
//             <button
//               disabled={currentPage === totalPages}
//               onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
//               className="px-4 py-2 border rounded disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         )}
//       </div>

//       <AddStaffDrawer
//         isOpen={isDrawerOpen}
//         editingStaff={editStaff}
//         onClose={() => {
//           setIsDrawerOpen(false);
//           setEditStaff(null);
//         }}
//         onSuccess={fetchStaff}
//       />

//       <ViewStaffDetailsModal
//         isOpen={!!viewStaff}
//         staff={viewStaff}
//         onClose={() => setViewStaff(null)}
//       />
//     </>
//   );
// };

// export default StaffManagement;

// import axios from "axios";
// import { Edit, Eye, MoreVertical, Trash2, Users } from "lucide-react";
// import { useEffect, useState } from "react";

// import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
// import Heading from "../../../components/AdminSideComponents/Heading";
// import AddStaffDrawer from "../../../components/AdminSideComponents/staff/AddStaffDrawer";
// import ViewStaffDetailsModal from "../../../components/AdminSideComponents/staff/ViewStaffDetailsModal";

// import { formatDate } from "../../../utils/helpers";
// import {
//   confirmAction,
//   showError,
//   showSuccess,
// } from "../../../utils/toastUtils";

// import { useAdminAuth } from "../../../context/AdminAuthContext";

// const ITEMS_PER_PAGE = 10;

// const StaffManagement = () => {
//   const { admin, isAdmin, isManager } = useAdminAuth();

//   const [staff, setStaff] = useState([]);
//   const [filteredStaff, setFilteredStaff] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);

//   const [selectedBranch, setSelectedBranch] = useState("");

//   const [currentPage, setCurrentPage] = useState(1);
//   const [actionOpen, setActionOpen] = useState(null);

//   const [isDrawerOpen, setIsDrawerOpen] = useState(false);
//   const [viewStaff, setViewStaff] = useState(null);
//   const [editStaff, setEditStaff] = useState(null);

//   const fetchStaff = async () => {
//     setIsLoading(true);
//     try {
//       const params = {};

//       if (isManager) {
//         params.branchId = admin?.assignedBranch;
//       }

//       const res = await axios.get(
//         `${process.env.REACT_APP_API_URL}/admins/get-all-staff`,
//         { params },
//       );

//       setStaff(res.data?.data || []);
//       setFilteredStaff(res.data?.data || []);
//     } catch (err) {
//       showError("Failed to fetch staff");
//       setStaff([]);
//       setFilteredStaff([]);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchStaff();
//   }, []);

//   const handleSearch = (term) => {
//     if (!term.trim()) {
//       setFilteredStaff(staff);
//       return;
//     }

//     const lower = term.toLowerCase();
//     const filtered = staff.filter(
//       (s) =>
//         s.name?.toLowerCase().includes(lower) ||
//         s.email?.toLowerCase().includes(lower) ||
//         s.phoneNumber?.includes(lower) ||
//         s.designation?.toLowerCase().includes(lower),
//     );

//     setFilteredStaff(filtered);
//     setCurrentPage(1);
//   };

//   const handleDeleteStaff = async (s) => {
//     const confirmed = await confirmAction({
//       title: "Delete Staff?",
//       text: `This will permanently remove ${s.name}`,
//       confirmText: "Delete",
//     });

//     if (!confirmed) return;

//     try {
//       await axios.delete(
//         `${process.env.REACT_APP_API_URL}/admins/delete/${s._id}`,
//       );
//       showSuccess("Staff deleted");
//       fetchStaff(selectedBranch);
//     } catch {
//       showError("Failed to delete staff");
//     }
//   };

//   const totalPages = Math.ceil(filteredStaff.length / ITEMS_PER_PAGE);
//   const paginatedStaff = filteredStaff.slice(
//     (currentPage - 1) * ITEMS_PER_PAGE,
//     currentPage * ITEMS_PER_PAGE,
//   );

//   return (
//     <>
//       <div className="px-4 py-3">
//         <Breadcrumb title1="Staff Management" title2="Staff" />
//       </div>

//       <div className="mx-4 mb-6 bg-white rounded-md shadow-lg p-6">
//         <Heading
//           onSearch={handleSearch}
//           showButton
//           buttonName="Add Staff"
//           onButtonClick={() => setIsDrawerOpen(true)}
//         />

//         {/* SUPER ADMIN BRANCH FILTER */}
//         {isAdmin && (
//           <div className="mb-4 max-w-xs">
//             <select
//               value={selectedBranch}
//               onChange={(e) => {
//                 setSelectedBranch(e.target.value);
//                 fetchStaff(e.target.value);
//               }}
//               className="w-full px-4 py-2 border rounded-lg"
//             >
//               <option value="">All Branches</option>
//               {branches.map((b) => (
//                 <option key={b._id} value={b._id}>
//                   {b.branchName}
//                 </option>
//               ))}
//             </select>
//           </div>
//         )}

//         <div className="overflow-x-auto no-scrollbar relative">
//           <table className="w-full table-fixed border border-gray-200 text-sm">
//             <thead className="bg-[#EDB5A4] text-xs uppercase">
//               <tr>
//                 <th className="p-3 w-12 text-center">#</th>
//                 <th className="p-3 w-40">Name</th>
//                 <th className="p-3 w-32">Phone</th>
//                 <th className="p-3 w-40">Email</th>
//                 <th className="p-3 w-56">Address</th>
//                 <th className="p-3 w-32">Staff Since</th>
//                 <th className="p-3 w-40">Designation</th>
//                 <th className="p-3 w-20 text-center">Action</th>
//               </tr>
//             </thead>

//             <tbody className="divide-y">
//               {isLoading ? (
//                 <tr>
//                   <td colSpan={8} className="py-16 text-center">
//                     Loading staff…
//                   </td>
//                 </tr>
//               ) : paginatedStaff.length === 0 ? (
//                 <tr>
//                   <td colSpan={8} className="py-16 text-center">
//                     No staff found
//                   </td>
//                 </tr>
//               ) : (
//                 paginatedStaff.map((s, index) => (
//                   <tr key={s._id} className="hover:bg-gray-50">
//                     <td className="p-3 text-center">
//                       {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
//                     </td>
//                     <td
//                       className="p-3 cursor-pointer"
//                       onClick={() => setViewStaff(s)}
//                     >
//                       {s.name}
//                     </td>
//                     <td className="p-3">{s.phoneNumber}</td>
//                     <td className="p-3 truncate">{s.email}</td>
//                     <td className="p-3 truncate">{s.address}</td>
//                     <td className="p-3">{formatDate(s.createdAt)}</td>
//                     <td className="p-3">{s.designation}</td>

//                     {/* ACTION DROPDOWN (FIXED TECHNIQUE) */}
//                     <td className="relative text-center">
//                       <button
//                         onClick={() =>
//                           setActionOpen((p) => (p === s._id ? null : s._id))
//                         }
//                         className="p-2 rounded-lg hover:bg-gray-200"
//                       >
//                         <MoreVertical size={18} />
//                       </button>

//                       {actionOpen === s._id && (
//                         <>
//                           <div
//                             className="fixed inset-0 z-10"
//                             onClick={() => setActionOpen(null)}
//                           />

//                           <div className="absolute right-10 top-8 w-48 bg-white rounded-lg shadow-xl border z-20">
//                             <button
//                               onClick={() => {
//                                 setViewStaff(s);
//                                 setActionOpen(null);
//                               }}
//                               className="w-full px-4 py-3 hover:bg-gray-100 flex gap-2"
//                             >
//                               <Eye size={16} /> View
//                             </button>

//                             <button
//                               onClick={() => {
//                                 setEditStaff(s);
//                                 setIsDrawerOpen(true);
//                                 setActionOpen(null);
//                               }}
//                               className="w-full px-4 py-3 hover:bg-blue-50 text-blue-600 flex gap-2"
//                             >
//                               <Edit size={16} /> Edit
//                             </button>

//                             <button
//                               onClick={() => handleDeleteStaff(s)}
//                               className="w-full px-4 py-3 hover:bg-red-50 text-red-600 flex gap-2"
//                             >
//                               <Trash2 size={16} /> Delete
//                             </button>
//                           </div>
//                         </>
//                       )}
//                     </td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       <AddStaffDrawer
//         isOpen={isDrawerOpen}
//         editingStaff={editStaff}
//         onClose={() => {
//           setIsDrawerOpen(false);
//           setEditStaff(null);
//         }}
//         onSuccess={() => fetchStaff(selectedBranch)}
//       />

//       <ViewStaffDetailsModal
//         isOpen={!!viewStaff}
//         staff={viewStaff}
//         onClose={() => setViewStaff(null)}
//       />
//     </>
//   );
// };

// export default StaffManagement;
